import React from 'react'

import { useTheme } from '@emotion/react'
import { useForm } from 'react-hook-form'

import { PaddingInset, PushCell, spacing, Stack } from '@cash-design-system/react'
import { ArcadeAvatarFromUiAvatar } from '@cash-web/avatar-from-proto'
import {
  TransferOptionSelectionBlocker,
  TransferOptionSelectionBlockerSavingsGoalIcon,
} from '@cash-web/protos/squareup/franklin/blockers/transfer_option_selection_blocker.pb'
import { ProgressPushCell } from '@cash-web/shared/ui-progress-cell'
import logger from '@cash-web/shared/util-logger'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationSelectTransferOption } from '@cash-web/shared-data-access-api/react-query/useMutationSelectTransferOption'
import { getEmojiSvg } from '@cash-web/shared-ui-emojis'

import { BaseBlocker, BaseBlockerFormFields, BlockerProps } from '../BaseBlocker/BaseBlocker'

type Props = BlockerProps & {
  blocker: TransferOptionSelectionBlocker
}

function GoalTransferOption({
  label,
  body,
  goalIcon,
  onClick,
}: {
  label: string
  body?: string
  goalIcon: TransferOptionSelectionBlockerSavingsGoalIcon
  onClick: () => void
}) {
  const { darkMode } = useTheme()

  return (
    <ProgressPushCell
      onClick={onClick}
      label={label}
      body={body}
      progressPercent={goalIcon.progress ?? 0}
      progressContent={
        <img alt="goal icon" src={getEmojiSvg({ iconId: goalIcon.icon_id ?? '', size: 24, darkMode })} />
      }
    />
  )
}

export function BlockerTransferOptionSelection({ blocker }: Props): React.ReactElement | null {
  const form = useForm<BaseBlockerFormFields>()
  const { mutateAsync: selectTransferOption } = useMutationSelectTransferOption()
  const { handleBlockerResponse } = useBlockerResponseHandler()

  const submit = async (optionId?: string) => {
    await handleBlockerResponse({
      promisedBlockerUpdate: selectTransferOption({
        select_option: {
          selected_option_id: optionId,
        },
      }),
      isResponseSuccess: response => {
        const blockerDescriptors = response.response_context?.scenario_plan?.blocker_descriptors
        return !!blockerDescriptors?.length && blockerDescriptors?.length > 0
      },
    })
  }

  const doNothing = () => {
    logger.error(new Error('TransferOptionSelection blocker was submitted in a way that should not be possible'))
  }

  return (
    <BaseBlocker
      mainText={blocker.title}
      secondaryText={blocker.subtitle}
      form={form}
      onSubmit={doNothing}
      buttons={[]}
    >
      <PaddingInset>
        <Stack space={spacing.xl}>
          {blocker.transfer_options?.map(transferOption => {
            const optionId = transferOption.option_id
            const label = transferOption.cell?.cell_default?.label_text?.translated_value ?? ''
            const body = transferOption.cell?.cell_default?.body_text?.translated_value

            return (
              <div key={optionId}>
                {transferOption.cell?.goal_icon ? (
                  <GoalTransferOption
                    key={optionId}
                    label={label}
                    body={body}
                    goalIcon={transferOption.cell?.goal_icon}
                    onClick={() => submit(optionId)}
                  />
                ) : (
                  <PushCell
                    type="button"
                    key={optionId}
                    label={label}
                    cellProps={{
                      body,
                      asset: {
                        avatar: <ArcadeAvatarFromUiAvatar uiAvatar={transferOption.cell?.ui_avatar} size="small" />,
                      },
                    }}
                    onClick={() => submit(optionId)}
                  />
                )}
              </div>
            )
          })}
        </Stack>
      </PaddingInset>
    </BaseBlocker>
  )
}
